export interface VoucherApplication {
  summary: string
  amount: number
  taxRate: number
}

export interface VoucherPrintData {
  company: string
  date: string
  voucherNo: string
  companyCode: string
  expenseAccount: string
  taxAccount: string
  payableAccount: string
  operator: string
  applications: VoucherApplication[]
  logoBase64?: string
  rows?: any[]
}

interface VoucherRow {
  summary: string
  account: string
  debit: number
  credit: number
}

interface VoucherPageData {
  rows: VoucherRow[]
  totalDebit: number
  totalCredit: number
  pageIndex: number
  totalPages: number
}

/** 金额转中文大写 */
export function numberToChinese(num: number): string {
  const cnNums = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
  const cnIntUnits = ['', '拾', '佰', '仟']
  const cnBigUnits = ['', '万', '亿', '兆']

  if (num === 0) return '零元整'

  const n = Math.abs(num)
  const s = n.toFixed(2)
  const [intStrRaw, decStr] = s.split('.')

  // 补齐到12位（亿级），每4位一组
  const intStr = intStrRaw.padStart(12, '0')

  let result = ''
  let needZero = false

  const groups = [
    { start: 0, unit: cnBigUnits[2] },   // 亿
    { start: 4, unit: cnBigUnits[1] },   // 万
    { start: 8, unit: '' }               // 个
  ]

  for (let g = 0; g < groups.length; g++) {
    const { start, unit } = groups[g]
    const groupStr = intStr.slice(start, start + 4)

    let groupResult = ''
    let zero = false
    let hasValue = false

    for (let i = 0; i < 4; i++) {
      const d = parseInt(groupStr[i])
      if (d === 0) {
        if (hasValue) zero = true
      } else {
        if (zero) {
          groupResult += '零'
          zero = false
        }
        groupResult += cnNums[d] + cnIntUnits[3 - i]
        hasValue = true
      }
    }

    if (hasValue) {
      if (needZero && result) {
        result += '零'
      }
      result += groupResult + unit
      needZero = false
    } else if (result && g < groups.length - 1) {
      needZero = true
    }
  }

  result = result.replace(/零+/g, '零').replace(/零$/, '')

  let finalResult = (result || '零') + '元'

  const jiao = parseInt(decStr[0])
  const fen = parseInt(decStr[1])
  const li = parseInt(decStr[2])
  const hao = parseInt(decStr[3])

  if (jiao === 0 && fen === 0) {
    finalResult += '整'
  } else {
    if (jiao > 0) finalResult += cnNums[jiao] + '角'
    if (fen > 0) {
      if (jiao === 0) finalResult += '零'
      finalResult += cnNums[fen] + '分'
    }
    if (li > 0) {
      if (jiao === 0) finalResult += '零'
      if (fen === 0) finalResult += '零'
      finalResult += cnNums[li] + '厘'
    }
    if (hao > 0) {
      if (jiao === 0) finalResult += '零'
      if (fen === 0) finalResult += '零'
      if (li === 0) finalResult += '零'
      finalResult += cnNums[hao] + '毫'
    }
  }

  return finalResult
}

const formatMoney = (v: unknown) => {
  if (v === null || v === undefined || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return ''
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function escapeHtml(text: string): string {
  if (text == null) return ''
  return text
    // .replace(/&/g, '&amp;')
    // .replace(/</g, '&lt;')
    // .replace(/>/g, '&gt;')
    // .replace(/"/g, '&quot;')
    // .replace(/'/g, '&#039;')
}

/** 将关联请款按每页最多2个分组，生成凭证页数据 */
function buildPages(data: VoucherPrintData): VoucherPageData[] {
  const itemsPerPage = 2
  const maxRows = 5

  const pages: VoucherPageData[] = []
  const totalPages = Math.ceil(data.applications.length / itemsPerPage)

  let rowIndex = 0
  const detailRows = data.rows || []

  for (let i = 0; i < data.applications.length; i += itemsPerPage) {
    const pageApps = data.applications.slice(i, i + itemsPerPage)
    const rows: VoucherRow[] = []
    let pageDebit = 0
    let pageCredit = 0

    for (const app of pageApps) {
      const taxRate = app.taxRate / 100
      const amountWithoutTax = app.amount / (1 + taxRate)
      const taxAmount = app.amount - amountWithoutTax

      // 费用行（借方）
      const expenseRow = detailRows[rowIndex]
      rows.push({
        summary: app.summary,
        account: expenseRow?.accountCode + '-' + expenseRow?.accountName || data.expenseAccount,
        debit: Math.round(amountWithoutTax * 100) / 100,
        credit: 0
      })
      pageDebit += Math.round(amountWithoutTax * 100) / 100
      if (detailRows.length > 0) rowIndex++

      // 税费行（借方）
      const taxRow = detailRows[rowIndex]
      if(taxRow.debit !== 0){
        rows.push({
          summary: app.summary,
          account: taxRow?.accountCode + '-' + taxRow?.accountName || data.taxAccount,
          debit: Math.round(taxAmount * 100) / 100,
          credit: 0
        })
      }
      pageDebit += Math.round(taxAmount * 100) / 100
      if (detailRows.length > 0) rowIndex++
    }

    // 本页总金额（贷方做平）
    const pageTotal = pageApps.reduce((sum, app) => sum + app.amount, 0)

    // 贷方行
    const payableRow = detailRows[rowIndex]
    const payableAccount = payableRow?.accountName || data.payableAccount
    rows.push({
      summary: '',
      account: payableAccount,
      debit: 0,
      credit: Math.round(pageTotal * 100) / 100
    })
    pageCredit += Math.round(pageTotal * 100) / 100
    if (detailRows.length > 0 && payableRow?.type === 'payable') rowIndex++

    // 补足空白行到5行
    while (rows.length < maxRows) {
      rows.push({ summary: '', account: '', debit: 0, credit: 0 })
    }

    pages.push({
      rows,
      totalDebit: Math.round(pageDebit * 100) / 100,
      totalCredit: Math.round(pageCredit * 100) / 100,
      pageIndex: Math.floor(i / itemsPerPage) + 1,
      totalPages
    })
  }

  return pages
}

function generateVoucherPageHTML(data: VoucherPrintData, page: VoucherPageData): string {
  const rowsHTML = page.rows.map(row => `
    <tr">
      <td class="col-summary">${escapeHtml(row.summary)}</td>
      <td class="col-account">${escapeHtml(row.account)}</td>
      <td class="col-debit">${formatMoney(row.debit)}</td>
      <td class="col-credit">${formatMoney(row.credit)}</td>
    </tr>
  `).join('')

  const totalChinese = numberToChinese(page.totalDebit)

  // <div class="info-item"><span class="info-label">核算单位：</span>${escapeHtml(data.company)}</div>
  return `
    <div class="voucher-page">
      <div class="voucher-header">
        <div class="logo-area">
          ${data.logoBase64 ? `<img src="${data.logoBase64}" style="height:28px;max-width:80px;object-fit:contain;" alt="logo" />` : '<img src="data:image/png;base64,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" style="height:28px;max-width:80px;object-fit:contain;" alt="logo" />'}
        </div>
        <div class="title-area">
          <h1 class="voucher-title">记账凭证</h1>
        </div>
        <div class="currency-area">
          <div>本币：人民币</div>
          <div>单位：元</div>
        </div>
      </div>

      <div class="voucher-info">
        <div class="info-item"><span class="info-label">账套：</span>${escapeHtml(data.companyCode)}</div>
        <div class="info-item"><span class="info-label">凭证日期：</span>${escapeHtml(data.date)}</div>
        <div class="info-item"><span class="info-label">凭证号码：</span>${escapeHtml(data.voucherNo)}</div>
      </div>

      <table class="voucher-table">
        <thead>
          <tr>
            <th class="col-summary">摘要</th>
            <th class="col-account">会计科目</th>
            <th class="col-debit">借方金额</th>
            <th class="col-credit">贷方金额</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHTML}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="2" class="total-label">
              <span>合计：${totalChinese}</span>
            </td>
            <td class="col-debit total-num">${formatMoney(page.totalDebit)}</td>
            <td class="col-credit total-num">${formatMoney(page.totalCredit)}</td>
          </tr>
        </tfoot>
      </table>

      <div class="voucher-footer">
        <div>批准：</div>
        <div>审核：</div>
        <div>部门：财务部</div>
        <div>经办人：${escapeHtml(data.operator)}</div>
      </div>

      ${page.totalPages > 1 ? `<div class="page-number">第${page.pageIndex}页 共${page.totalPages}页</div>` : ''}
    </div>
  `
}

function generatePrintCSS(): string {
  return `
    @page { size: 240mm 140mm; margin: 6mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: "SimSun", "宋体", serif; font-size: 12pt; color: #000; background: #fff; }

    .voucher-page {
      max-width: 100%;
      margin: 0 auto;
      padding: 4mm 6mm;
      box-sizing: border-box;
      page-break-after: always;
    }
    .voucher-page:last-child {
      page-break-after: auto;
    }

    .voucher-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4mm;
    }
    .logo-area { width: 60px; flex-shrink: 0; }
    .logo {
      width: 50px; height: 24px;
      background: #333; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-weight: bold; font-size: 14px;
      letter-spacing: 2px;
    }
    .title-area { flex: 1; text-align: center; }
    .voucher-title { font-size: 22pt; font-weight: bold; letter-spacing: 8px; margin: 0; }
    .currency-area { text-align: right; font-size: 10pt; line-height: 1.6; flex-shrink: 0; }

    .voucher-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 3mm;
      font-size: 11pt;
    }
    .info-label { font-weight: bold; }

    .voucher-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11pt;
      table-layout: fixed;
    }
    .voucher-table th, .voucher-table td {
      border: 1px solid #000;
      padding: 5px 8px;
      text-align: center;
      vertical-align: middle;
      height: 34px;
    }
    .voucher-table th {
      font-weight: bold;
      background: #f9f9f9;
      height: 28px;
    }
    .col-summary { width: 42%; text-align: left !important; }
    .col-account { width: 36%; text-align: left !important; }
    .col-debit { width: 11%; text-align: right !important; }
    .col-credit { width: 11%; text-align: right !important; }

    .total-label { text-align: left !important; font-weight: bold; padding-left: 12px !important; }
    .total-num { font-weight: bold; }

    .voucher-footer {
      display: flex;
      justify-content: space-between;
      margin-top: 6mm;
      font-size: 11pt;
    }
    .voucher-footer > div { flex: 1; }

    .page-number {
      text-align: center;
      margin-top: 3mm;
      font-size: 10pt;
      color: #666;
    }
  `
}

/** 生成预览用HTML（适配弹窗内白色背景区域） */
export function generateVoucherPreviewHTML(data: VoucherPrintData): string {
  const pages = buildPages(data)
  const pagesHTML = pages.map(p => generateVoucherPageHTML(data, p)).join('')

  return `
    <style>
      .voucher-preview-wrap { font-family: "SimSun", "宋体", serif; font-size: 11pt; color: #000; }
      .voucher-preview-wrap .voucher-page {
        width: 100%; max-width: 100%; margin: 0 auto; padding: 8px 12px;
        border: 1px solid #ddd; margin-bottom: 12px; background: #fff;
      }
      .voucher-preview-wrap .voucher-page:last-child { margin-bottom: 0; }
      .voucher-preview-wrap .voucher-header {
        display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;
      }
      .voucher-preview-wrap .logo-area { width: auto; flex-shrink: 0; }
      .voucher-preview-wrap .logo-area img { height: 22px; max-width: 60px; object-fit: contain; }
      .voucher-preview-wrap .logo {
        width: 36px; height: 18px; background: #333; color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-weight: bold; font-size: 11px; letter-spacing: 1px;
      }
      .voucher-preview-wrap .title-area { flex: 1; text-align: center; }
      .voucher-preview-wrap .voucher-title { font-size: 16pt; font-weight: bold; letter-spacing: 6px; margin: 0; }
      .voucher-preview-wrap .currency-area { text-align: right; font-size: 9pt; line-height: 1.5; }
      .voucher-preview-wrap .voucher-info {
        display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 10pt;
      }
      .voucher-preview-wrap .info-label { font-weight: bold; }
      .voucher-preview-wrap .voucher-table {
        width: 100%; border-collapse: collapse; font-size: 10pt; table-layout: fixed;
      }
      .voucher-preview-wrap .voucher-table th,
      .voucher-preview-wrap .voucher-table td {
        border: 1px solid #333; padding: 4px 6px; text-align: center; vertical-align: middle; height: 28px;
      }
      .voucher-preview-wrap .voucher-table th {
        font-weight: bold; background: #f5f5f5; height: 24px;
      }
      .voucher-preview-wrap .col-summary { width: 32%; text-align: left !important; }
      .voucher-preview-wrap .col-account { width: 36%; text-align: left !important; }
      .voucher-preview-wrap .col-debit { width: 16%; text-align: right !important; }
      .voucher-preview-wrap .col-credit { width: 16%; text-align: right !important; }
      .voucher-preview-wrap .total-label { text-align: left !important; font-weight: bold; }
      .voucher-preview-wrap .total-num { font-weight: bold; }
      .voucher-preview-wrap .voucher-footer {
        display: flex; justify-content: space-between; margin-top: 8px; font-size: 10pt;
      }
      .voucher-preview-wrap .voucher-footer > div { flex: 1; }
      .voucher-preview-wrap .page-number {
        text-align: center; margin-top: 4px; font-size: 9pt; color: #666;
      }
    </style>
    <div class="voucher-preview-wrap">
      ${pagesHTML}
    </div>
  `
}

/** 唤起浏览器打印 */
export function printVoucher(data: VoucherPrintData): void {
  const pages = buildPages(data)
  const pagesHTML = pages.map(p => generateVoucherPageHTML(data, p)).join('')

  const fullHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>记账凭证打印</title>
      <style>${generatePrintCSS()}</style>
    </head>
    <body>
      ${pagesHTML}
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 300);
        };
        window.addEventListener('afterprint', function() {
          setTimeout(function() { window.close(); }, 200);
        }, { once: true });
      <\/script>
    </body>
    </html>
  `

  const blob = new Blob([fullHTML], { type: 'text/html;charset=utf-8' })
  const blobUrl = URL.createObjectURL(blob)

  const w = window.open(blobUrl, '_blank', 'popup=yes,width=1100,height=800')
  if (!w) {
    URL.revokeObjectURL(blobUrl)
    throw new Error('无法打开打印窗口，请允许本站弹出窗口后重试')
  }

  const cleanup = () => {
    try { URL.revokeObjectURL(blobUrl) } catch {}
  }

  w.addEventListener('afterprint', cleanup, { once: true })
  setTimeout(cleanup, 120_000)
}

/**
 * 打印完整凭证：直接遍历 getPrintDetail 返回的 rows，每页固定5行，
 * 不足补空行，只在最后一页显示合计。
 */
export function printVoucherAllReceipt(data: VoucherPrintData): void {
  const maxRowsPerPage = 5

  const allRows = (data.rows || []).map((r: any) => ({
    summary: r.summary || '',
    account: r.accountCode && r.accountName
      ? `${r.accountCode}-${r.accountName}`
      : (r.accountName || r.account || ''),
    debit: Number(r.debit),
    credit: Number(r.credit)
  }))

  if (allRows.length === 0) {
    throw new Error('没有可打印的凭证数据')
  }

  const totalPages = Math.ceil(allRows.length / maxRowsPerPage)
  const totalDebit = allRows.reduce((sum, r) => sum + r.debit, 0)
  const totalCredit = allRows.reduce((sum, r) => sum + r.credit, 0)
  const totalChinese = numberToChinese(totalDebit)

  const pagesHTML: string[] = []

  for (let i = 0; i < totalPages; i++) {
    const pageRows = allRows.slice(i * maxRowsPerPage, (i + 1) * maxRowsPerPage)
    while (pageRows.length < maxRowsPerPage) {
      pageRows.push({ summary: '', account: '', debit: null, credit: null })
    }

    const isLastPage = i === totalPages - 1
    const pageIndex = i + 1

    const rowsHTML = pageRows.map(row => `
      <tr>
        <td class="col-summary">${escapeHtml(row.summary)}</td>
        <td class="col-account">${escapeHtml(row.account)}</td>
        <td class="col-debit">${formatMoney(row.debit)}</td>
        <td class="col-credit">${formatMoney(row.credit)}</td>
      </tr>
    `).join('')

    const totalHTML = isLastPage ? `
      <tfoot>
        <tr>
          <td colspan="2" class="total-label">
            <span>合计：${totalChinese}</span>
          </td>
          <td class="col-debit total-num">${formatMoney(totalDebit)}</td>
          <td class="col-credit total-num">${formatMoney(totalCredit)}</td>
        </tr>
      </tfoot>
    ` : ''

    const logoHtml = data.logoBase64
      ? `<img src="${data.logoBase64}" style="height:28px;max-width:80px;object-fit:contain;" alt="logo" />`
      : '<div class="logo">JF</div>'

    // <div class="info-item"><span class="info-label">核算单位：</span>${escapeHtml(data.company)}</div>
    
    const pageHTML = `
      <div class="voucher-page">
        <div class="voucher-header">
          <div class="logo-area">${logoHtml}</div>
          <div class="title-area">
            <h1 class="voucher-title">记账凭证</h1>
          </div>
          <div class="currency-area">
            <div>本币：人民币</div>
            <div>单位：元</div>
          </div>
        </div>

        <div class="voucher-info">
          <div class="info-item"><span class="info-label">账套：</span>${escapeHtml(data.companyCode)}</div>
          <div class="info-item"><span class="info-label">凭证日期：</span>${escapeHtml(data.date)}</div>
          <div class="info-item"><span class="info-label">凭证号码：</span>${escapeHtml(data.voucherNo)}</div>
        </div>

        <table class="voucher-table">
          <thead>
            <tr>
              <th class="col-summary">摘要</th>
              <th class="col-account">会计科目</th>
              <th class="col-debit">借方金额</th>
              <th class="col-credit">贷方金额</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHTML}
          </tbody>
          ${totalHTML}
        </table>

        <div class="voucher-footer">
          <div>批准：</div>
          <div>审核：</div>
          <div>部门：财务部</div>
          <div>经办人：${escapeHtml(data.operator)}</div>
        </div>

        ${totalPages > 1 ? `<div class="page-number">第${pageIndex}页 共${totalPages}页</div>` : ''}
      </div>
    `
    pagesHTML.push(pageHTML)
  }

  const fullHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>记账凭证打印</title>
      <style>${generatePrintCSS()}</style>
    </head>
    <body>
      ${pagesHTML.join('')}
      <script>
        window.onload = function() {
          setTimeout(function() { window.print(); }, 300);
        };
        window.addEventListener('afterprint', function() {
          setTimeout(function() { window.close(); }, 200);
        }, { once: true });
      <\/script>
    </body>
    </html>
  `

  const blob = new Blob([fullHTML], { type: 'text/html;charset=utf-8' })
  const blobUrl = URL.createObjectURL(blob)

  const w = window.open(blobUrl, '_blank', 'popup=yes,width=1100,height=800')
  if (!w) {
    URL.revokeObjectURL(blobUrl)
    throw new Error('无法打开打印窗口，请允许本站弹出窗口后重试')
  }

  const cleanup = () => {
    try { URL.revokeObjectURL(blobUrl) } catch {}
  }

  w.addEventListener('afterprint', cleanup, { once: true })
  setTimeout(cleanup, 120_000)
}
